import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';

export const US_PHONE_DISPLAY_PLACEHOLDER = '000-000-0000';

const phoneUtil = PhoneNumberUtil.getInstance();

/** Display a stored or partial phone value as xxx-xxx-xxxx for US numbers. */
export function formatUsPhoneDisplay(value: string | null | undefined): string {
  if (value == null) return '';
  const trimmed = value.trim();
  if (!trimmed) return '';

  try {
    const parsed = phoneUtil.parse(trimmed, trimmed.startsWith('+') ? undefined : 'US');
    const region = phoneUtil.getRegionCodeForNumber(parsed);
    if (region === 'US') {
      const national = phoneUtil.getNationalSignificantNumber(parsed);
      if (national.length === 10) {
        return formatNationalDigits(national);
      }
    }
    return phoneUtil.format(parsed, PhoneNumberFormat.INTERNATIONAL);
  } catch {
    const digits = trimmed.replace(/\D/g, '');
    const national =
      digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits.slice(-10);
    if (national.length === 10) {
      return formatNationalDigits(national);
    }
    return trimmed;
  }
}

/** Format up to 10 national digits as xxx-xxx-xxxx while typing. */
export function formatUsPhoneInputDigits(digits: string): string {
  const national =
    digits.length > 10 && digits.startsWith('1') ? digits.slice(1, 11) : digits.slice(0, 10);
  if (!national) return '';
  let out = national.slice(0, 3);
  if (national.length > 3) out += `-${national.slice(3, 6)}`;
  if (national.length > 6) out += `-${national.slice(6, 10)}`;
  return out;
}

/** Resolve ngx-intl-tel-input or stored value to canonical E.164 when possible. */
export function resolveContactNumber(value: unknown): string | null {
  if (value == null) return null;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? tryParseToE164(trimmed) ?? trimmed : null;
  }

  if (typeof value === 'object') {
    const phone = value as Record<string, unknown>;
    const raw =
      phone['e164Number'] ??
      phone['internationalNumber'] ??
      phone['nationalNumber'] ??
      phone['number'];
    if (typeof raw !== 'string') return null;
    const trimmed = raw.trim();
    return trimmed ? tryParseToE164(trimmed) ?? trimmed : null;
  }

  return null;
}

/** Value for ngx-intl-tel-input writeValue so the field shows xxx-xxx-xxxx. */
export function toPhoneFormValue(stored: string | null | undefined): unknown {
  if (!stored) return null;

  const trimmed = stored.trim();
  if (!trimmed) return null;

  try {
    const parsed = phoneUtil.parse(trimmed, trimmed.startsWith('+') ? undefined : 'US');
    const e164 = phoneUtil.format(parsed, PhoneNumberFormat.E164);
    const countryCode = (phoneUtil.getRegionCodeForNumber(parsed) || 'US').toLowerCase();
    const dialCode = `+${parsed.getCountryCode()}`;
    const nationalDashed = formatUsPhoneDisplay(e164);

    return {
      number: nationalDashed,
      internationalNumber: phoneUtil.format(parsed, PhoneNumberFormat.INTERNATIONAL),
      nationalNumber: nationalDashed,
      e164Number: e164,
      countryCode: countryCode.toUpperCase(),
      dialCode,
    };
  } catch {
    return trimmed;
  }
}

function tryParseToE164(raw: string): string | null {
  try {
    const parsed = phoneUtil.parse(raw, raw.startsWith('+') ? undefined : 'US');
    return phoneUtil.format(parsed, PhoneNumberFormat.E164);
  } catch {
    return null;
  }
}

function formatNationalDigits(national: string): string {
  return `${national.slice(0, 3)}-${national.slice(3, 6)}-${national.slice(6)}`;
}
